// Dragging an activity row into a different place in the lesson.
//
// The handle is what carries `draggable`, not the row: a row holds textareas,
// and a draggable ancestor makes selecting text inside them fight the drag.
// This is the same arrangement the deck's content blocks use — see
// ContentBlocks/ContentBlock.js.
//
// The handle is also a real button, so the rows can be reordered from the
// keyboard: focus it and press the up or down arrow. Drag-and-drop on its own
// is unusable without a mouse, and a lesson plan is exactly the kind of
// document people rearrange.

import React, { useState } from 'react';
import { GripVertical } from 'lucide-react';

/** Marks a row as a drop target; the hook finds the drag image through it. */
export const ACTIVITY_ROW_ATTRIBUTE = 'data-activity-row';

/**
 * Drag-to-reorder for a list of rows.
 *
 * @param {(from: number, to: number) => void} onReorder
 * @param {number} count how many rows there are, for the keyboard bounds
 */
export const useActivityReorder = (onReorder, count) => {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const reset = () => {
    setDraggingIndex(null);
    setDropIndex(null);
  };

  /** Spread onto the row element. */
  const rowProps = (index) => ({
    [ACTIVITY_ROW_ATTRIBUTE]: index,
    onDragOver: (event) => {
      if (draggingIndex === null) return;
      // Without preventDefault the browser refuses the drop outright.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dropIndex !== index) setDropIndex(index);
    },
    onDragLeave: () => {
      setDropIndex((current) => (current === index ? null : current));
    },
    onDrop: (event) => {
      event.preventDefault();
      if (draggingIndex !== null && draggingIndex !== index) onReorder(draggingIndex, index);
      reset();
    }
  });

  /** Spread onto the handle. */
  const handleProps = (index) => ({
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.effectAllowed = 'move';
      // Some browsers cancel a drag that carries no data.
      event.dataTransfer.setData('text/plain', String(index));
      // Drag the whole row, not the little handle.
      const row = event.currentTarget.closest(`[${ACTIVITY_ROW_ATTRIBUTE}]`);
      if (row && event.dataTransfer.setDragImage) {
        event.dataTransfer.setDragImage(row, 20, 20);
      }
      setDraggingIndex(index);
    },
    onDragEnd: reset,
    onKeyDown: (event) => {
      const to = event.key === 'ArrowUp' ? index - 1 : event.key === 'ArrowDown' ? index + 1 : null;
      if (to === null) return;
      event.preventDefault();
      if (to >= 0 && to < count) onReorder(index, to);
    }
  });

  /** Border feedback for the row currently under the pointer. */
  const rowClassName = (index) => {
    if (draggingIndex === null) return '';
    if (index === draggingIndex) return ' opacity-50';
    if (index === dropIndex) return ' ring-2 ring-inset ring-blue-500';
    return '';
  };

  return { draggingIndex, dropIndex, rowProps, handleProps, rowClassName };
};

const ActivityDragHandle = ({ index, count, ...dragProps }) => (
  <button
    type="button"
    className="cursor-grab rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 active:cursor-grabbing"
    title="Drag to reorder — or use the up and down arrow keys"
    aria-label={`Reorder activity ${index + 1} of ${count}`}
    {...dragProps}
  >
    <GripVertical size={16} />
  </button>
);

export default ActivityDragHandle;
